import ErrorMessage from "@/v2-ui/ErrorMessage"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useUserUsageQuery } from "@/v2-backoffice-client/user/usage/user.usage.query"
import { APP_CODE_LABELS } from "@/v2-backoffice-client/usage/usage.constants"
import UsageMcpReport from "@/v2-backoffice-client/usage/mcp/UsageMcpReport"
import { useUsageMcpUserQuery } from "@/v2-backoffice-client/usage/mcp/usage.mcpUser.query"
import { MCP_HISTORY_DAYS } from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { hasMcpActivity } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import UserUsageOverview from "@/v2-backoffice-client/user/usage/UserUsageOverview"
import UserUsageFusion from "@/v2-backoffice-client/user/usage/UserUsageFusion"
import {
  USER_USAGE_HISTORY_DAYS,
  USER_USAGE_TABS_ID,
} from "@/v2-backoffice-client/user/usage/user.usage.constants"

export type UserUsageProps = {
  userId: number
  /** The person's name as the sheet shows it, for titles and prose. */
  subject: string
}

// Usage tab of the user panel: one overview, then one page per product.
// This component owns the reads and the tabs; each tab is its own
// component with its own derivations and state. The profile is computed
// daily in Databricks from console and MCP tracking events and synced into
// the app DB (acc.UserUsageProfiles / acc.UserUsageAreasDaily).
// Observations only — CRM workflow stays in HubSpot.
function UserUsage(props: UserUsageProps) {
  const { userId, subject } = props

  const { data, loading, error } = useUserUsageQuery({
    variables: { userId, days: USER_USAGE_HISTORY_DAYS },
  })
  const datasetQuery = useUsageMcpUserQuery({
    variables: { userId, days: MCP_HISTORY_DAYS, appCode: "mcp-dataset" },
  })

  const profile = data?.getUsageProfileByUser
  const areaDays = data?.getUsageAreasDailyByUser ?? []
  const hasDataset = hasMcpActivity(
    datasetQuery.data?.getUsageMcpByUser.Days ?? [],
  )

  if (loading && !profile) {
    return <p className="p-4 text-sm text-muted-foreground">Loading…</p>
  }

  if (!profile) {
    return (
      <div className="flex flex-col gap-3 p-1">
        <ErrorMessage error={error} />
        <p className="usage-report-empty">
          No tracked usage yet. Profiles are computed every night from Fusion
          and MCP activity; a person with no tracked events (or only activity
          from before December 2025) has none.
        </p>
      </div>
    )
  }

  return (
    <Tabs
      id={USER_USAGE_TABS_ID}
      defaultValue="overview"
      className="flex flex-col gap-6"
    >
      <Tabs.List className="self-start">
        <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
        <Tabs.Trigger value="fusion">Fusion</Tabs.Trigger>
        <Tabs.Trigger value="placepoint-mcp">
          {APP_CODE_LABELS["mcp-placepoint"]}
        </Tabs.Trigger>
        {hasDataset && (
          <Tabs.Trigger value="dataset-mcp">
            {APP_CODE_LABELS["mcp-dataset"]}
          </Tabs.Trigger>
        )}
      </Tabs.List>

      <Tabs.Content value="overview">
        <UserUsageOverview
          userId={userId}
          subject={subject}
          profile={profile}
          areaDays={areaDays}
          error={error}
        />
      </Tabs.Content>

      <Tabs.Content value="fusion">
        <UserUsageFusion
          subject={subject}
          profile={profile}
          areaDays={areaDays}
        />
      </Tabs.Content>

      <Tabs.Content value="placepoint-mcp">
        <div className="usage-report">
          <UsageMcpReport
            userId={userId}
            appCode="mcp-placepoint"
            productName={APP_CODE_LABELS["mcp-placepoint"]}
            subject={subject}
          />
        </div>
      </Tabs.Content>

      {hasDataset && (
        <Tabs.Content value="dataset-mcp">
          <div className="usage-report">
            <UsageMcpReport
              userId={userId}
              appCode="mcp-dataset"
              productName={APP_CODE_LABELS["mcp-dataset"]}
              subject={subject}
            />
          </div>
        </Tabs.Content>
      )}
    </Tabs>
  )
}

export default UserUsage
